/**
 * SlugField — shared inline editor for community URL slugs.
 *
 * Originally lived inside CommunitySettingsModal/CommunityEntitiesTab.tsx
 * (DEV-888). Extracted to a shared component for DEV-939 so the same
 * editor can be used by GeographyAdminModal for geo entities.
 *
 * The editor:
 *   - Shows live charset + length validation feedback
 *   - Suggests an auto-slug from the entity name (one-click accept)
 *   - Renders a live URL preview
 *
 * Behavior is identical for org and geo entities. The parent component
 * is responsible for sending the value to the right API endpoint and
 * for surfacing server-side rejection (taken / reserved) errors.
 *
 * URL preview:
 *   - Top-level entities (no parent) → "life4earth.com/<slug>"
 *   - Sub-entities → "life4earth.com/.../<slug>" by default
 *   - Optional `previewPath` prop overrides the default with a caller-
 *     computed path (e.g. "life4earth.com/usa/california/<slug>") so
 *     the geo admin can show the full path it'll resolve to.
 */

import React from "react";
import { slugifyName } from "../../api";

export interface SlugFieldProps {
  /** Current slug value. Empty string means unset. */
  value: string;
  /** Entity name. Drives auto-suggestion and the placeholder. */
  name: string;
  /** Called on every keystroke with the raw input value. */
  onChange: (value: string) => void;
  /**
   * Optional context: the GUID of the org-root or geo-top-level. When
   * present, the URL preview shows "/..." for the parent path; when
   * absent, the preview treats this entity as top-level and shows just
   * the slug. Pass when editing a sub-entity.
   */
  parentContextGuid?: string;
  /** Display name fallback for preview when `value` and `suggestion` are both empty. */
  entityName: string;
  /**
   * Optional caller-supplied URL preview path. When provided, replaces
   * the default "life4earth.com/.../{slug}" with the concrete path
   * (e.g. "life4earth.com/usa/california/{slug}"). The component still
   * appends the slug placeholder.
   */
  previewPath?: string;
  /** Disable the input and the suggest button. */
  disabled?: boolean;
}

const SlugField: React.FC<SlugFieldProps> = ({
  value,
  name,
  onChange,
  parentContextGuid,
  entityName,
  previewPath,
  disabled,
}) => {
  const suggestion = React.useMemo(() => slugifyName(name), [name]);
  const showSuggest = !value.trim() && !!suggestion;
  const charsetOk =
    !value.trim() || /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value.trim());
  const lenOk = !value.trim() || value.trim().length <= 64;
  const valid = charsetOk && lenOk;

  const previewSlug =
    value.trim() ||
    suggestion ||
    entityName.toLowerCase().replace(/\s+/g, "-");
  const renderedSlug = previewSlug || "<slug>";
  const previewUrl = previewPath
    ? `${previewPath}${renderedSlug}`
    : parentContextGuid
      ? `life4earth.com/.../${renderedSlug}`
      : `life4earth.com/${renderedSlug}`;

  return (
    <div style={{ marginBlockStart: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBlockEnd: 4,
        }}
      >
        <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
          URL Slug
        </span>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
          (lowercase letters, digits, hyphens; max 64)
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={suggestion || "e.g. jamestown"}
          disabled={disabled}
          maxLength={64}
          style={{
            flex: 1,
            padding: "8px 10px",
            borderRadius: 8,
            border: `1px solid ${valid ? "var(--border-subtle)" : "rgba(248,113,113,0.55)"}`,
            background: "var(--bg-tertiary)",
            color: "var(--text-primary)",
            fontSize: 13,
            outline: "none",
          }}
        />
        {showSuggest && (
          <button
            type="button"
            onClick={() => onChange(suggestion)}
            disabled={disabled}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid var(--border-default)",
              background: "transparent",
              color: "var(--text-primary)",
              fontSize: 12,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Use "{suggestion}"
          </button>
        )}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-muted)",
          marginBlockStart: 4,
        }}
      >
        Preview: <code>{previewUrl}</code>
      </div>
      {!valid && (
        <div
          style={{
            fontSize: 11,
            color: "rgba(252,165,165,1)",
            marginBlockStart: 4,
          }}
        >
          {!charsetOk
            ? "Invalid characters. Use lowercase letters, digits, and hyphens only (no leading/trailing hyphens)."
            : "Slug exceeds 64 characters."}
        </div>
      )}
    </div>
  );
};

export default SlugField;
